/**
 * What this browser remembers of an organisation's brand, so the console header can paint the
 * customer's name and logo on the first frame of a reload instead of a beat later.
 *
 * Per product and organisation (`<storagePrefix>.brand.<orgId>`). A convenience, never a source:
 * the brand query replaces it whenever `updatedAt` differs, and every read and write survives a
 * browser that refuses storage. Only a small logo is kept — a `data:` URL is about a third larger
 * than the file, and a 2 MB logo would spend most of what a page may store.
 */
import { PRODUCT } from '@/product/profile';

export interface KeptBrand {
  updatedAt: string | null;
  name: string | null;
  /** The logo as a `data:` URL, or null when there is none or it was too large to keep. */
  dataUrl: string | null;
}

/** About 110 KB of image. A logo larger than that is fetched each session instead. */
export const MAX_KEPT_DATA_URL = 150_000;

const keyFor = (orgId: string): string => `${PRODUCT.storagePrefix}.brand.${orgId}`;

/** The part of `Storage` this needs; injected by the tests, which run without a browser. */
export type BrandStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function browserStore(): BrandStore | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function isKept(v: unknown): v is KeptBrand {
  if (v === null || typeof v !== 'object') return false;
  const o = v as Record<string, unknown>;
  const opt = (x: unknown) => x === null || typeof x === 'string';
  return opt(o.updatedAt) && opt(o.name) && opt(o.dataUrl) && (o.dataUrl === null || String(o.dataUrl).startsWith('data:image/'));
}

export function readKeptBrand(orgId: string, store: BrandStore | null = browserStore()): KeptBrand | null {
  if (!orgId || store === null) return null;
  try {
    const raw = store.getItem(keyFor(orgId));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return isKept(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Keep a brand; a brand with neither name nor logo is forgotten instead. */
export function keepBrand(orgId: string, brand: KeptBrand, store: BrandStore | null = browserStore()): void {
  if (!orgId || store === null) return;
  try {
    if (brand.name === null && brand.dataUrl === null) {
      store.removeItem(keyFor(orgId));
      return;
    }
    const dataUrl = brand.dataUrl !== null && brand.dataUrl.length <= MAX_KEPT_DATA_URL ? brand.dataUrl : null;
    store.setItem(keyFor(orgId), JSON.stringify({ ...brand, dataUrl }));
  } catch {
    /* A browser that refuses storage paints the brand a beat later, which is all this saves. */
  }
}
